import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "../../components/layout/Header";
import SendInviteModal from "../../components/client/SendInviteModal";
import { assignedAgentOptions } from "../../js/api/clientActivity";
import { createClientInvite, inviteHref, listDeskInvites } from "../../js/api/clientInvite";
import { useAuth } from "../../js/hooks/useAuth";
import { useDesk } from "../../js/hooks/useDesk";
import { hasBrokerAccess } from "../../js/auth/authService";

const EMPTY = {
  kind: "buyer",
  name: "",
  email: "",
  phone: "",
  address: "",
  notes: "",
  assignedAgentId: "",
};

export default function ClientRegister() {
  const { session } = useAuth();
  const desk = useDesk();
  const variant = hasBrokerAccess(session?.role) && session?.role !== "agent" ? "broker" : "agent";
  const agents = assignedAgentOptions(session);
  const [form, setForm] = useState({
    ...EMPTY,
    assignedAgentId: session?.role === "agent" ? session.userId : agents[0]?.id || "",
  });
  const [error, setError] = useState("");
  const [invite, setInvite] = useState(null);
  const [tick, setTick] = useState(0);
  const pending = useMemo(
    () => listDeskInvites(session).filter((row) => row.status === "pending"),
    [session, tick]
  );

  function update(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  function submit(event) {
    event.preventDefault();
    setError("");
    try {
      const row = createClientInvite({ session, ...form });
      setInvite(row);
      setForm({
        ...EMPTY,
        assignedAgentId: session?.role === "agent" ? session.userId : agents[0]?.id || "",
      });
      setTick((value) => value + 1);
    } catch (err) {
      setError(err.message || "The invitation did not save.");
    }
  }

  return (
    <AppLayout variant={variant}>
      <article className="start-here">
        <div className="page-header">
          <div>
            <p className="eyebrow">Invite first</p>
            <h1>Invite a client</h1>
            <p className="page-lead">
              Send a REChecklist invitation. The client completes setup. Only then is a Client ID
              created and linked to this advisor. Buyer and seller stay on separate carpets.
            </p>
          </div>
        </div>
        <form className="start-here__card start-here__form" onSubmit={submit}>
          <fieldset className="start-here__roles">
            <legend>This client is</legend>
            <label className={form.kind === "buyer" ? "is-on" : ""}>
              <input type="radio" name="kind" value="buyer" checked={form.kind === "buyer"} onChange={update} />
              A buyer
            </label>
            <label className={form.kind === "seller" ? "is-on" : ""}>
              <input type="radio" name="kind" value="seller" checked={form.kind === "seller"} onChange={update} />
              A seller
            </label>
          </fieldset>
          <label className="field">
            <span className="field-label">Client name</span>
            <input className="field-input" name="name" value={form.name} onChange={update} required />
          </label>
          <label className="field">
            <span className="field-label">Email</span>
            <input className="field-input" name="email" type="email" value={form.email} onChange={update} required />
          </label>
          <label className="field">
            <span className="field-label">Phone (optional)</span>
            <input className="field-input" name="phone" value={form.phone} onChange={update} />
          </label>
          <label className="field">
            <span className="field-label">Property address (optional)</span>
            <input className="field-input" name="address" value={form.address} onChange={update} />
          </label>
          <label className="field">
            <span className="field-label">Notes (optional)</span>
            <textarea className="field-input" name="notes" rows={3} value={form.notes} onChange={update} />
          </label>
          <label className="field">
            <span className="field-label">Assigned agent</span>
            <select className="field-select" name="assignedAgentId" value={form.assignedAgentId} onChange={update}>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name}
                </option>
              ))}
              {!agents.length ? <option value={session.userId}>{session.name || "This desk"}</option> : null}
            </select>
          </label>
          {session.role === "broker" ? <p className="muted">Assigned broker: {session.name}</p> : null}
          <button className="primary-btn" type="submit">
            Prepare invitation
          </button>
          {error ? <p className="start-here__error">{error}</p> : null}
          <p className="muted">
            <Link to={desk.clients}>Client Manager</Link>
            {" · "}
            <Link to={desk.startHere}>Start Here</Link>
          </p>
        </form>
        {pending.length ? (
          <section className="start-here__card">
            <h2>Waiting on setup</h2>
            <ul className="account-sessions">
              {pending.map((row) => (
                <li key={row.token}>
                  {row.name} · {row.email} · {row.kind === "seller" ? "Seller" : "Buyer"}
                  <div className="muted">{inviteHref(row.token)}</div>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </article>
      <SendInviteModal
        invite={invite}
        isOpen={Boolean(invite)}
        onClose={() => {
          setInvite(null);
          setTick((value) => value + 1);
        }}
      />
    </AppLayout>
  );
}
